import React, {useEffect, useMemo, useRef} from 'react';

export type Props = {
  value: string;
  valueLength: number;
  onChange: (value: string) => void;
  disabled?: boolean;
  timer?: string;
  Error?: string;
  isLoading?: boolean;
};

const RE_DIGIT = new RegExp(/^\d+$/);

export default function OtpInput({
  value,
  valueLength,
  onChange,
  disabled = false,
  timer,
  Error,
  isLoading,
}: Props) {
  const inputsRef = useRef<HTMLInputElement[]>([]);

  // Focus first input on mount with a small delay for modal animation
  useEffect(() => {
    const timer = setTimeout(() => {
      if (inputsRef.current[0]) {
        inputsRef.current[0].focus();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [isLoading]);

  const valueItems = useMemo(() => {
    const valueArray = value.split('');
    const items: Array<string> = [];

    for (let i = 0; i < valueLength; i++) {
      const char = valueArray[i];

      if (RE_DIGIT.test(char)) {
        items.push(char);
      } else {
        items.push('');
      }
    }

    return items;
  }, [value, valueLength]);

  const focusToNextInput = (target: HTMLElement) => {
    const nextElementSibling =
      target.nextElementSibling as HTMLInputElement | null;

    if (nextElementSibling) {
      nextElementSibling.focus();
    }
  };

  const focusToPrevInput = (target: HTMLElement) => {
    const previousElementSibling =
      target.previousElementSibling as HTMLInputElement | null;

    if (previousElementSibling) {
      previousElementSibling.focus();
    }
  };

  const inputOnChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    idx: number,
  ) => {
    const target = e.target;

    // Prevent changes if input already has a value (max length reached)
    if (target.value.length === valueLength - 1) {
      return;
    }

    let targetValue = target.value.trim();
    const isTargetValueDigit = RE_DIGIT.test(targetValue);

    if (!isTargetValueDigit && targetValue !== '') {
      return;
    }

    const nextInputEl = target.nextElementSibling as HTMLInputElement | null;

    // only delete digit if next input element has no value
    if (!isTargetValueDigit && nextInputEl && nextInputEl.value !== '') {
      return;
    }

    targetValue = isTargetValueDigit ? targetValue : ' ';

    const targetValueLength = targetValue.length;

    if (targetValueLength === 1) {
      const newValue =
        value.substring(0, idx) + targetValue + value.substring(idx + 1);

      onChange(newValue);

      if (!isTargetValueDigit) {
        return;
      }

      focusToNextInput(target);
    } else if (targetValueLength === valueLength) {
      onChange(targetValue);

      target.blur();
    }
  };

  const inputOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const {key} = e;
    const target = e.target as HTMLInputElement;

    if (key === 'ArrowRight' || key === 'ArrowDown') {
      e.preventDefault();
      return focusToNextInput(target);
    }

    if (key === 'ArrowLeft' || key === 'ArrowUp') {
      e.preventDefault();
      return focusToPrevInput(target);
    }

    const targetValue = target.value;

    // keep the selection range position
    // if the same digit was typed
    target.setSelectionRange(0, targetValue.length);

    if (e.key !== 'Backspace' || targetValue !== '') {
      return;
    }

    focusToPrevInput(target);
  };

  const inputOnFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    const {target} = e;

    // keep focusing back until previous input
    // element has value
    const prevInputEl =
      target.previousElementSibling as HTMLInputElement | null;

    if (prevInputEl && prevInputEl.value === '') {
      return prevInputEl.focus();
    }

    target.setSelectionRange(0, target.value.length);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, valueLength);
    if (pasted) {
      onChange(pasted.padEnd(valueLength, ' ')); // or just pasted
    }
  };

  return (
    <div className="flex flex-col ">
      <div
        className="flex  w-full max-w-[360px] justify-center items-center gap-3 mb-3 mt-1"
        dir="ltr"
      >
        {valueItems.map((digit, idx) => (
          <input
            key={idx}
            ref={el => (inputsRef.current[idx] = el!)}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="\d{1}"
            //   maxLength={valueLength}
            maxLength={1}
            className=" p-0 text-lg font-medium text-center border rounded-[4px] size-9 border-gray-500 selection:bg-transparent caret-transparent outline-none focus-visible:border-gray-400 focus-visible:ring-2 focus-visible:ring-gray-300
"
            value={digit}
            onChange={e => {
              if (isLoading) return;
              inputOnChange(e, idx);
            }}
            onKeyDown={inputOnKeyDown}
            onFocus={inputOnFocus}
            onPaste={handlePaste}
            disabled={disabled}
          />
        ))}
      </div>

      {Error && (
        <div className="flex items-center justify-center gap-1 pt-2 pb-6 text-red-600">
          <i className="text-sm icon-warning-triangle" />
          <span className="text-sm font-normal">{Error}</span>
        </div>
      )}
      <span className="text-center text-gray-800 text-md"> {timer}</span>
    </div>
  );
}
